import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { LabelComponent } from '../../../shared/components/form/label/label.component';
import { SelectComponent } from '../../../shared/components/form/select/select.component';
import { InputFieldComponent } from '../../../shared/components/form/input/input-field.component';
import { NotificationService } from '../../../shared/components/ui/notification/notification.service';

import { DocumentSeriesService } from '../../../services/document-series.service';
import { DocumentSeriesResponse } from '../../../dto/document-series.response';
import { DocumentSeriesCreateRequest } from '../../../dto/document-series-create.request';
import { DocumentSeriesUpdateRequest } from '../../../dto/document-series-update.request';

@Component({
    selector: 'app-series',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        PageBreadcrumbComponent,
        ButtonComponent,
        BadgeComponent,
        ModalComponent,
        LabelComponent,
        SelectComponent,
        InputFieldComponent,
    ],
    templateUrl: './series.component.html',
})
export class SeriesComponent implements OnInit, OnDestroy {

    // ── Pagination ────────────────────────────────────────────────
    currentPage = 1;
    itemsPerPage = 10;
    readonly pageSizeOptions = [5, 10, 15, 20, 50];

    // ── Sort ──────────────────────────────────────────────────────
    sortColumn = '';
    sortDir: 'asc' | 'desc' = 'asc';

    // ── UI ────────────────────────────────────────────────────────
    loading = false;
    loadingTable = false;
    searchTerm = '';

    // ── Data ──────────────────────────────────────────────────────
    seriesList: DocumentSeriesResponse[] = [];
    private sub = new Subscription();

    // ── Form modal ────────────────────────────────────────────────
    showForm = false;
    isEditMode = false;
    submitted = false;
    isSubmitting = false;
    editingId: number | null = null;
    editingSequence = 0;

    formDocTypeCode = '';
    formSeriesValue = '';
    formParentDocTypeCode = '';

    // ── Confirm toggle ────────────────────────────────────────────
    showConfirmToggle = false;
    toggleTarget?: DocumentSeriesResponse;
    isToggling = false;

    readonly documentTypeOptions = [
        { value: '01', label: '01 - Factura' },
        { value: '03', label: '03 - Boleta de Venta' },
        { value: '07', label: '07 - Nota de Crédito' },
        { value: '08', label: '08 - Nota de Débito' },
        { value: '09', label: '09 - Guía de Remisión' },
    ];

    readonly parentDocTypeOptions = [
        { value: '01', label: '01 - Factura' },
        { value: '03', label: '03 - Boleta de Venta' },
    ];

    constructor(
        private documentSeriesService: DocumentSeriesService,
        private notify: NotificationService,
    ) {}

    ngOnInit(): void { this.loadSeries(); }
    ngOnDestroy(): void { this.sub.unsubscribe(); }

    // ── Load ──────────────────────────────────────────────────────

    loadSeries(): void {
        this.loading = true;
        this.loadingTable = true;
        const s = this.documentSeriesService.findAll().subscribe({
            next: res => {
                this.seriesList = res.data ?? [];
                this.currentPage = 1;
                this.loading = false;
                this.loadingTable = false;
            },
            error: err => {
                this.loading = false;
                this.loadingTable = false;
                this.seriesList = [];
                this.notify.error(err?.error?.message ?? 'Error al cargar series');
            },
        });
        this.sub.add(s);
    }

    // ── Search / Filter / Sort ────────────────────────────────────

    setSearchTerm(value: string): void {
        this.searchTerm = (value ?? '').trim().toLowerCase();
        this.currentPage = 1;
    }

    private matchesSearch(s: DocumentSeriesResponse): boolean {
        if (!this.searchTerm) return true;
        return (
            s.documentTypeSunatName.toLowerCase().includes(this.searchTerm) ||
            s.documentTypeSunatCode.includes(this.searchTerm) ||
            s.series.toLowerCase().includes(this.searchTerm)
        );
    }

    get filteredList(): DocumentSeriesResponse[] {
        let list = this.seriesList.filter(s => this.matchesSearch(s));
        if (this.sortColumn === 'tipo') {
            list = [...list].sort((a, b) =>
                this.sortDir === 'asc'
                    ? a.documentTypeSunatName.localeCompare(b.documentTypeSunatName, 'es')
                    : b.documentTypeSunatName.localeCompare(a.documentTypeSunatName, 'es')
            );
        }
        return list;
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredList.length / this.itemsPerPage));
    }

    get currentItems(): DocumentSeriesResponse[] {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.filteredList.slice(start, start + this.itemsPerPage);
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) this.currentPage = page;
    }

    onPageSizeChange(size: number): void {
        this.itemsPerPage = size;
        this.currentPage = 1;
    }

    toggleSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDir = 'asc';
        }
    }

    // ── Form ──────────────────────────────────────────────────────

    get requiresParentDocType(): boolean {
        return this.formDocTypeCode === '07' || this.formDocTypeCode === '08';
    }

    get editingDocTypeName(): string {
        return this.documentTypeOptions.find(o => o.value === this.formDocTypeCode)?.label ?? this.formDocTypeCode;
    }

    get seriesFormError(): string {
        if (!this.isEditMode && !this.formDocTypeCode) return 'Tipo de documento requerido';
        if (!this.isEditMode && this.requiresParentDocType && !this.formParentDocTypeCode)
            return 'Debes indicar si la serie es para Factura o Boleta de Venta';
        if (!this.formSeriesValue.trim()) return 'Serie requerida';
        if (this.formSeriesValue.trim().length > 4) return 'Serie máximo 4 caracteres';
        return '';
    }

    onCreateSeries(): void {
        this.isEditMode = false;
        this.editingId = null;
        this.editingSequence = 0;
        this.formDocTypeCode = '';
        this.formSeriesValue = '';
        this.formParentDocTypeCode = '';
        this.submitted = false;
        this.showForm = true;
    }

    onEditSeries(s: DocumentSeriesResponse): void {
        this.isEditMode = true;
        this.editingId = s.id;
        this.editingSequence = s.sequence ?? 0;
        this.formDocTypeCode = s.documentTypeSunatCode;
        this.formSeriesValue = s.series;
        this.formParentDocTypeCode = s.parentDocumentTypeCode ?? '';
        this.submitted = false;
        this.showForm = true;
    }

    onCloseForm(): void {
        this.showForm = false;
        this.submitted = false;
    }

    onSubmit(): void {
        this.submitted = true;
        if (this.seriesFormError) return;
        this.isSubmitting = true;

        const createReq: DocumentSeriesCreateRequest = {
            documentTypeCode: this.formDocTypeCode,
            series: this.formSeriesValue.trim(),
            ...(this.requiresParentDocType && this.formParentDocTypeCode
                ? { parentDocumentTypeCode: this.formParentDocTypeCode }
                : {}),
        };

        const op$ = this.isEditMode
            ? this.documentSeriesService.update(this.editingId!, { series: this.formSeriesValue.trim() } as DocumentSeriesUpdateRequest)
            : this.documentSeriesService.create(createReq);

        const s = op$.subscribe({
            next: res => {
                this.notify.success(res.message ?? 'Guardado correctamente');
                this.isSubmitting = false;
                this.showForm = false;
                this.loadSeries();
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al guardar serie');
                this.isSubmitting = false;
            },
        });
        this.sub.add(s);
    }

    // ── Toggle status ─────────────────────────────────────────────

    openToggleConfirm(s: DocumentSeriesResponse): void {
        this.toggleTarget = s;
        this.showConfirmToggle = true;
    }

    closeToggleConfirm(): void {
        this.showConfirmToggle = false;
        this.toggleTarget = undefined;
    }

    get toggleActionLabel(): string {
        return this.toggleTarget?.status === 1 ? 'Inactivar' : 'Activar';
    }

    get toggleConfirmText(): string {
        if (!this.toggleTarget) return '';
        const action = this.toggleTarget.status === 1 ? 'inactivar' : 'activar';
        return `¿Deseas ${action} la serie ${this.toggleTarget.series}?`;
    }

    confirmToggleStatus(): void {
        if (!this.toggleTarget) return;
        this.isToggling = true;
        const target = this.toggleTarget;

        const s = this.documentSeriesService.toggleStatus(target.id).subscribe({
            next: res => {
                this.notify.success(res.message ?? 'Estado actualizado');
                this.closeToggleConfirm();
                this.loadSeries();
                this.isToggling = false;
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al cambiar estado');
                this.isToggling = false;
            },
        });
        this.sub.add(s);
    }

    getBadgeColor(status: number): 'success' | 'error' {
        return status === 1 ? 'success' : 'error';
    }

    getBadgeText(status: number): string {
        return status === 1 ? 'Activo' : 'Inactivo';
    }
}
